import React from 'react';
import './FeatureShowcase.css';

export interface FeatureItem {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
}

export interface FeatureShowcaseProps {
  title?: string;
  subtitle?: string;
  features: FeatureItem[];
  columns?: 1 | 2 | 3;
  className?: string;
  layout?: 'grid' | 'zigzag';
}

/**
 * 功能展示组件
 * 以网格布局展示功能列表，每个功能包含标题、描述和图片
 */
const FeatureShowcase: React.FC<FeatureShowcaseProps> = ({
  title,
  subtitle,
  features,
  columns = 3,
  className = '',
  layout = 'grid',
}) => {
  // 根据列数确定网格类名
  const gridClassName = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  }[columns];

  return (
    <div className={`feature-showcase ${className}`}>
      {/* 标题区域 */}
      {(title || subtitle) && (
        <div className="text-center mb-12">
          {title && <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{title}</h2>}
          {subtitle && <p className="text-lg text-gray-600 max-w-3xl mx-auto">{subtitle}</p>}
        </div>
      )}

      {layout === 'grid' ? (
        // 网格布局
        <div className={`grid ${gridClassName} gap-8`}>
          {features.map((feature) => (
            <div key={feature.id} className="feature-card bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
              {/* 图片区域 - 参考首页图片大小 */}
              <div className="phone-mockup relative z-10 rounded-2xl overflow-hidden bg-background">
                <img 
                  src={feature.imageUrl} 
                  alt={feature.title} 
                  className="max-h-[500px] md:max-h-[400px] sm:max-h-[350px] w-auto object-contain"
                />
              </div>
              
              {/* 内容区域 */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Z字形交错布局
        <div className="space-y-16">
          {features.map((feature, index) => (
            <div 
              key={feature.id} 
              className={`flex flex-col md:flex-row items-center gap-8 ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}
            >
              {/* 内容区域 */}
              <div className="flex-1 p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-lg text-gray-600">{feature.description}</p>
              </div>
              
              {/* 图片区域 - 参考首页图片大小 */}
              <div className="flex-1">
                <div className="phone-mockup relative z-10 rounded-2xl overflow-hidden bg-background">
                  <img 
                    src={feature.imageUrl} 
                    alt={feature.title} 
                    className="max-h-[500px] md:max-h-[400px] sm:max-h-[350px] w-auto object-contain mx-auto"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeatureShowcase;